import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogProps
} from '@mui/material';
import theme from 'App.theme';
import { RootState } from 'storage/store';
import { blockBodyPosition, resetBodyPosition } from 'utils/bodyPosition';
import { Icon } from 'components/Icon';
import { EMPTY_ARRAY_LENGTH } from 'utils/consts';
import { ModalContainer } from './styled/ModalContainer';
import { ModalContentWrapper } from './styled/ModalContentWrapper';

export interface IModal extends DialogProps {
  heading?: JSX.Element | string;
  content?: string;
  actions?: JSX.Element;
  withoutCloseIcon?: boolean;
}

export interface IModalContent {
  fullScreen?: boolean;
}

const Modal = ({
  heading,
  content,
  actions,
  withoutCloseIcon = false,
  children,
  ...props
}: IModal) => {
  const { statements } = useSelector((state: RootState) => state.statements);

  const handleClose = (
    event: Object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick') {
      props.onClose?.(event, reason);
    }
  };

  useEffect(() => {
    const isNotAcceptedStatement =
      statements.filter((statement) => !statement.accepted).length >
      EMPTY_ARRAY_LENGTH;

    if (props.open || isNotAcceptedStatement) {
      blockBodyPosition();
      return;
    }
    if (document.body.style.position === 'fixed' && !props.open) {
      resetBodyPosition();
    }
  }, [props.open]);

  return (
    <ModalContainer
      {...props}
      disableScrollLock
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') return;
        handleClose(event, reason);
      }}
    >
      <DialogTitle>
        {heading}
        {!withoutCloseIcon ? (
          <Icon
            icon="cancel"
            color="primary"
            fontSize="medium"
            onClick={(event: Object) => handleClose(event, 'escapeKeyDown')}
            sx={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.light
              }
            }}
          />
        ) : null}
      </DialogTitle>
      <ModalContentWrapper fullScreen={props.fullScreen}>
        <DialogContent>{children || content}</DialogContent>
      </ModalContentWrapper>
      {actions ? <DialogActions>{actions}</DialogActions> : null}
    </ModalContainer>
  );
};

export default Modal;
